import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import {
    Badge, Box, Button, ButtonGroup, Collapse, Flex, Input, Select, Spinner,
    Tab, TabList, TabPanel, TabPanels, Tabs, Td, Text, Textarea,
    NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
    useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Row, Table, Tag } from "components"
import { API } from "config"
import { useAllDistributors } from "hooks"
import { Authenticate } from "screens"

// ─── Hooks ────────────────────────────────────────────────────────────────────

const useDistributorLots = (distributorId, term = '') => useQuery({
    queryKey: ['distributor-lots', distributorId, term],
    queryFn: async () => {
        const params = new URLSearchParams({ per_page: 100, ...(term && { term }) })
        const { data } = await API.get(`/distributors/${distributorId}/stock/lots?${params}`)
        return data
    },
    enabled: !!distributorId,
    staleTime: 15000,
})

const useLossesList = (page = 1, filters = {}) => useQuery({
    queryKey: ['stock-losses', page, filters],
    queryFn: async () => {
        const params = new URLSearchParams({
            page,
            ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        })
        const { data } = await API.get(`/stock/losses?${params}`)
        return data
    },
})

// ─── Input de data nativo (sem conversão UTC) ─────────────────────────────────

const DateInput = ({ label, value, onChange }) => {
    const borderColor = useColorModeValue('#CBD5E0', '#4A5568')
    const bg          = useColorModeValue('white',   '#2D3748')
    const color       = useColorModeValue('#1A202C', '#E2E8F0')
    const labelC      = useColorModeValue('gray.500', 'gray.400')
    return (
        <Box>
            <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                letterSpacing="wider" color={labelC} mb={1}>{label}
            </Text>
            <input
                type="date" value={value} onChange={e => onChange(e.target.value)}
                style={{
                    height: '40px', padding: '0 12px', fontSize: '14px',
                    borderRadius: '6px', border: `1px solid ${borderColor}`,
                    background: bg, color, width: '155px', outline: 'none',
                }}
            />
        </Box>
    )
}

// ─── Validade com cor contextual ──────────────────────────────────────────────

const ExpiryBadge = ({ date }) => {
    if (!date) return <Text fontSize="xs" color="gray.400">Sem val.</Text>

    const days = Math.ceil((new Date(date) - new Date()) / 86400000)
    const fmt   = new Date(date).toLocaleDateString('pt-BR')

    if (days < 0)   return <Badge colorScheme="red"    fontSize="xs">Vencido · {fmt}</Badge>
    if (days <= 30) return <Badge colorScheme="red"    fontSize="xs">{days}d · {fmt}</Badge>
    if (days <= 90) return <Badge colorScheme="orange" fontSize="xs">{days}d · {fmt}</Badge>
    return              <Badge colorScheme="green"  fontSize="xs">{fmt}</Badge>
}

// ─── Formulário inline de registro de perda ───────────────────────────────────

const InlineLossForm = ({ lot, distributorId, onSuccess, onCancel }) => {
    const authenticate   = useDisclosure()
    const toast          = useToast()
    const queryClient    = useQueryClient()
    const [qty, setQty]  = useState(1)
    const [reason, setReason] = useState('')
    const [loading, setLoading] = useState(false)
    const [errors, setErrors]   = useState({})

    const bg     = useColorModeValue('red.50',   'gray.750')
    const border = useColorModeValue('red.100',  'red.800')
    const labelC = useColorModeValue('gray.600', 'gray.300')

    const validate = () => {
        const e = {}
        if (!qty || qty < 1)          e.qty    = 'Mínimo 1'
        if (qty > lot.quantity)        e.qty    = `Máximo: ${lot.quantity} un.`
        if (reason.trim().length < 10) e.reason = 'Mínimo 10 caracteres'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleConfirm = () => { if (validate()) authenticate.onOpen() }

    const handleSubmit = async () => {
        setLoading(true)
        try {
            const res = await API.post('/stock/losses', {
                product_id:     lot.product.id,
                distributor_id: distributorId,
                product_lot_id: lot.id,
                quantity:       Number(qty),
                reason:         reason.trim(),
            })
            // Aprovação automática — a senha inserida no Authenticate é a assinatura do supervisor
            await API.post(`/stock/losses/${res.data.id}/approve`)
            toast({ title: 'Perda registrada e aprovada', status: 'success', duration: 3000 })
            queryClient.invalidateQueries({ queryKey: ['stock-losses'] })
            queryClient.invalidateQueries({ queryKey: ['distributor-lots', distributorId] })
            onSuccess?.()
        } catch (err) {
            toast({ title: err?.response?.data?.message ?? 'Erro ao registrar perda', status: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Box bg={bg} border="1px solid" borderColor={border} borderRadius="lg" p={5} mt={2} mb={3}>
                {/* Contexto */}
                <Flex align="center" gap={2} mb={5} flexWrap="wrap">
                    <Icon name="loss" boxSize={4} color="red.400" />
                    <Text fontSize="sm" fontWeight="bold" color="red.600" _dark={{ color: 'red.300' }}>
                        {lot.product.name}
                    </Text>
                    <Tag colorScheme="purple" size="sm">{lot.lot_number}</Tag>
                    <Text fontSize="xs" color="gray.400">·</Text>
                    <Text fontSize="xs" color="gray.500">
                        Disponível: <Box as="span" fontWeight="bold" color="red.500">{lot.quantity} un.</Box>
                    </Text>
                </Flex>

                <Flex gap={5} flexWrap="wrap" align="flex-start">
                    {/* Quantidade */}
                    <Box minW="160px">
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelC} mb={2}>
                            Quantidade perdida *
                        </Text>
                        <NumberInput
                            size="md" min={1} max={lot.quantity} value={qty}
                            onChange={v => { setQty(Number(v)); setErrors(e => ({ ...e, qty: null })) }}
                            isInvalid={!!errors.qty}
                        >
                            <NumberInputField borderRadius="md" />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        {errors.qty && <Text fontSize="xs" color="red.500" mt={1}>{errors.qty}</Text>}
                    </Box>

                    {/* Motivo */}
                    <Box flex={1} minW="280px">
                        <Flex justify="space-between" mb={2}>
                            <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                                letterSpacing="wider" color={labelC}>
                                Motivo da perda *
                            </Text>
                            <Text fontSize="xs" color={reason.length < 10 ? 'red.400' : 'gray.400'}>
                                {reason.length}/500
                            </Text>
                        </Flex>
                        <Textarea
                            size="md" rows={3} resize="none"
                            placeholder="Ex: Produto vencido, avaria no transporte, furto..."
                            value={reason}
                            onChange={e => { setReason(e.target.value); setErrors(err => ({ ...err, reason: null })) }}
                            isInvalid={!!errors.reason}
                            borderRadius="md"
                        />
                        {errors.reason && <Text fontSize="xs" color="red.500" mt={1}>{errors.reason}</Text>}
                    </Box>
                </Flex>

                {/* Ações */}
                <Flex justify="flex-end" gap={2} mt={4}>
                    <Button size="md" variant="ghost" onClick={onCancel} isDisabled={loading}>
                        Cancelar
                    </Button>
                    <Button size="md" colorScheme="red" isLoading={loading}
                        leftIcon={<Icon name="check" boxSize={4} />}
                        onClick={handleConfirm}>
                        Confirmar Perda
                    </Button>
                </Flex>
            </Box>

            <Authenticate {...authenticate} onSuccess={handleSubmit} />
        </>
    )
}

// ─── Linha de lote com expansão inline ───────────────────────────────────────

const LotRow = ({ lot, distributorId, expandedId, onExpand, onSuccess }) => {
    const isExpanded = expandedId === lot.id
    const rowBg      = useColorModeValue(
        isExpanded ? 'red.50'  : undefined,
        isExpanded ? 'gray.700' : undefined,
    )

    return (
        <>
            <Row bg={rowBg} transition="background 0.15s">
                <Td>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
                            {lot.product?.name}
                        </Text>
                        {lot.product?.cod && (
                            <Text fontSize="xs" color="gray.400">Cód: {lot.product.cod}</Text>
                        )}
                    </Box>
                </Td>
                <Td>
                    <Tag colorScheme="purple" size="sm" fontFamily="mono">{lot.lot_number}</Tag>
                </Td>
                <Td>
                    <ExpiryBadge date={lot.expires_at} />
                </Td>
                <Td isNumeric>
                    <Text fontWeight="bold" fontSize="sm"
                        color={lot.quantity <= 5 ? 'orange.500' : 'inherit'}>
                        {lot.quantity}
                        {lot.quantity <= 5 && (
                            <Text as="span" fontSize="xs" fontWeight="normal" color="orange.400" ml={1}>
                                un.
                            </Text>
                        )}
                        {lot.quantity > 5 && <Text as="span" fontSize="xs" color="gray.400" ml={1}>un.</Text>}
                    </Text>
                </Td>
                <Td isNumeric>
                    <Button
                        size="xs"
                        colorScheme={isExpanded ? 'gray' : 'red'}
                        variant={isExpanded ? 'solid' : 'outline'}
                        leftIcon={<Icon name={isExpanded ? 'cancel' : 'loss'} boxSize={3} />}
                        onClick={() => onExpand(isExpanded ? null : lot.id)}
                    >
                        {isExpanded ? 'Cancelar' : 'Registrar Perda'}
                    </Button>
                </Td>
            </Row>
            {isExpanded && (
                <tr>
                    <td colSpan={5} style={{ padding: '0 12px', border: 'none' }}>
                        <InlineLossForm
                            lot={lot}
                            distributorId={distributorId}
                            onSuccess={() => { onExpand(null); onSuccess?.() }}
                            onCancel={() => onExpand(null)}
                        />
                    </td>
                </tr>
            )}
        </>
    )
}

// ─── Tab 1: Registrar perda ───────────────────────────────────────────────────

const RegisterTab = ({ warehouses }) => {
    const [distributorId, setDistributorId] = useState('')
    const [term, setTerm]                   = useState('')
    const [debouncedTerm, setDebounced]     = useState('')
    const [expandedId, setExpandedId]       = useState(null)

    const filterBg     = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor   = useColorModeValue('gray.500', 'gray.400')

    useEffect(() => {
        const t = setTimeout(() => setDebounced(term), 350)
        return () => clearTimeout(t)
    }, [term])

    const { data, isFetching, refetch } = useDistributorLots(distributorId, debouncedTerm)
    const lots = data?.data ?? []

    const columns = [
        { children: 'Produto' },
        { children: 'Lote' },
        { children: 'Validade' },
        { children: 'Disponível', isNumeric: true },
        { children: '', isNumeric: true },
    ]

    return (
        <Box>
            {/* Filtros */}
            <Box bg={filterBg} border="1px solid" borderColor={filterBorder} borderRadius="xl" p={4} mb={5}>
                <Flex align="center" gap={2} mb={3}>
                    <Icon name="search" boxSize={3.5} color={labelColor} />
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="widest" color={labelColor}>
                        Selecionar Estoque
                    </Text>
                </Flex>
                <Flex gap={3} flexWrap="wrap" align="flex-end">
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Núcleo *
                        </Text>
                        <Select size="md" w="260px" value={distributorId}
                            onChange={e => { setDistributorId(e.target.value); setExpandedId(null) }}>
                            <option value="">Selecione um núcleo</option>
                            {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Buscar produto
                        </Text>
                        <Input size="md" w="300px" value={term}
                            placeholder="Nome do produto..."
                            isDisabled={!distributorId}
                            onChange={e => setTerm(e.target.value)} />
                    </Box>
                    <IconButton size="md" icon="refresh" isLoading={isFetching}
                        isDisabled={!distributorId}
                        onClick={refetch} />
                </Flex>
            </Box>

            {!distributorId ? (
                <Flex justify="center" align="center" direction="column" gap={3} py={16} color="gray.300">
                    <Icon name="stock" boxSize={12} />
                    <Text fontSize="sm" color="gray.400">
                        Selecione um núcleo para ver os lotes disponíveis.
                    </Text>
                </Flex>
            ) : isFetching ? (
                <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
            ) : lots.length === 0 ? (
                <Flex justify="center" align="center" direction="column" gap={3} py={16} color="gray.300">
                    <Icon name="loss" boxSize={10} />
                    <Text fontSize="sm" color="gray.400">
                        {debouncedTerm
                            ? `Nenhum lote encontrado para "${debouncedTerm}".`
                            : 'Nenhum lote com saldo disponível neste núcleo.'}
                    </Text>
                </Flex>
            ) : (
                <>
                    <Flex align="center" gap={2} mb={2}>
                        <Text fontSize="xs" color={labelColor}>
                            {lots.length} lote{lots.length !== 1 ? 's' : ''} com saldo disponível
                        </Text>
                        {data?.meta?.total > lots.length && (
                            <Badge colorScheme="gray" fontSize="xs">
                                mostrando {lots.length} de {data.meta.total}
                            </Badge>
                        )}
                    </Flex>
                    <Table columns={columns}>
                        {lots.map(lot => (
                            <LotRow
                                key={lot.id}
                                lot={lot}
                                distributorId={distributorId}
                                expandedId={expandedId}
                                onExpand={setExpandedId}
                                onSuccess={refetch}
                            />
                        ))}
                    </Table>
                </>
            )}
        </Box>
    )
}

// ─── Tab 2: Histórico de perdas ────────────────────────────────────────────────

const HistoryTab = ({ warehouses }) => {
    const navigate = useNavigate()
    const [page, setPage]                          = useState(1)
    const [filterDistributorId, setFilterDist]     = useState('')
    const [filterLotNumber, setFilterLot]          = useState('')
    const [filterInvoiceNumber, setFilterInvoice]  = useState('')
    const [filterFrom, setFilterFrom]              = useState('')
    const [filterTo, setFilterTo]                  = useState('')

    const filters = {
        ...(filterDistributorId  && { distributor_id: filterDistributorId }),
        ...(filterLotNumber      && { lot_number: filterLotNumber }),
        ...(filterInvoiceNumber  && { invoice_number: filterInvoiceNumber }),
        ...(filterFrom           && { from: filterFrom }),
        ...(filterTo             && { to: filterTo }),
    }

    const losses       = useLossesList(page, filters)
    const filterBg     = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor   = useColorModeValue('gray.500', 'gray.400')
    const hasFilters   = !!(filterDistributorId || filterLotNumber || filterInvoiceNumber || filterFrom || filterTo)

    const clearFilters = () => {
        setFilterDist('')
        setFilterLot('')
        setFilterInvoice('')
        setFilterFrom('')
        setFilterTo('')
        setPage(1)
    }

    const columns = [
        { children: 'Produto' },
        { children: 'Lote' },
        { children: 'NF-e' },
        { children: 'Núcleo' },
        { children: 'Quantidade', isNumeric: true },
        { children: 'Motivo' },
        { children: 'Registrado por' },
        { children: 'Aprovação' },
        { children: 'Data', isNumeric: true },
    ]

    return (
        <Box>
            <Box bg={filterBg} border="1px solid" borderColor={filterBorder} borderRadius="xl" p={4} mb={5}>
                <Flex justify="space-between" align="center" mb={3}>
                    <Flex align="center" gap={2}>
                        <Icon name="search" boxSize={3.5} color={labelColor} />
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="widest" color={labelColor}>
                            Filtros
                        </Text>
                        {hasFilters && <Box w="6px" h="6px" borderRadius="full" bg="red.400" />}
                    </Flex>
                    <Flex gap={2} align="center">
                        {hasFilters && (
                            <Button size="sm" colorScheme="red" variant="outline" onClick={clearFilters}
                                leftIcon={<Icon name="close" boxSize={3.5} />} borderRadius="full">
                                Limpar Filtros
                            </Button>
                        )}
                        <IconButton size="sm" icon="refresh" isLoading={losses.isFetching}
                            onClick={losses.refetch} />
                    </Flex>
                </Flex>

                <Flex gap={3} flexWrap="wrap" align="flex-end">
                    {/* Núcleo */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Núcleo
                        </Text>
                        <Select size="md" w="240px" value={filterDistributorId}
                            onChange={e => { setFilterDist(e.target.value); setPage(1) }}>
                            <option value="">Todos os núcleos</option>
                            {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                    </Box>

                    {/* Número da nota */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Número da NF-e
                        </Text>
                        <Input
                            size="md" w="160px" value={filterInvoiceNumber}
                            placeholder="Ex: 001234"
                            fontFamily="mono"
                            onChange={e => { setFilterInvoice(e.target.value); setPage(1) }}
                        />
                    </Box>

                    {/* Número do lote */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Número do lote
                        </Text>
                        <Input
                            size="md" w="160px" value={filterLotNumber}
                            placeholder="Ex: LOT-2024-01"
                            fontFamily="mono"
                            onChange={e => { setFilterLot(e.target.value); setPage(1) }}
                        />
                    </Box>

                    {/* Período */}
                    <DateInput label="De" value={filterFrom}
                        onChange={v => { setFilterFrom(v); setPage(1) }} />
                    <DateInput label="Até" value={filterTo}
                        onChange={v => { setFilterTo(v); setPage(1) }} />
                </Flex>
            </Box>

            {losses.data?.data?.length === 0 && !losses.isFetching ? (
                <Flex justify="center" align="center" direction="column" gap={3} py={12} color="gray.300">
                    <Icon name="loss" boxSize={10} />
                    <Text fontSize="sm" color="gray.400">
                        {hasFilters
                            ? 'Nenhuma perda encontrada com os filtros aplicados.'
                            : 'Nenhuma perda registrada ainda.'}
                    </Text>
                </Flex>
            ) : (
                <Table columns={columns} setPage={setPage} meta={losses.data?.meta}>
                    {losses.data?.data?.map(item => <LossHistoryRow key={item.id} data={item} />)}
                </Table>
            )}
        </Box>
    )
}

const LossHistoryRow = ({ data }) => {
    const navigate = useNavigate()
    return (
    <Row>
        <Td>
            <Text fontSize="sm" fontWeight="medium">{data?.product?.name}</Text>
        </Td>
        <Td>
            {data?.lot_number
                ? <Tag colorScheme="purple" size="sm" fontFamily="mono">{data.lot_number}</Tag>
                : <Text color="gray.400" fontSize="sm">—</Text>}
        </Td>
        <Td>
            {data?.invoice_number ? (
                <Box
                    as="button"
                    onClick={() => navigate('/stock/invoices', { state: { search: data.invoice_number } })}
                    fontFamily="mono" fontSize="xs" fontWeight="semibold"
                    color="teal.500" _hover={{ color: 'teal.300', textDecoration: 'underline' }}
                    transition="color 0.15s"
                >
                    NF #{data.invoice_number}
                </Box>
            ) : (
                <Text color="gray.400" fontSize="xs">—</Text>
            )}
        </Td>
        <Td fontSize="sm">{data?.distributor?.name}</Td>
        <Td isNumeric>
            <Text fontWeight="bold" color="red.500" fontSize="sm">−{data?.quantity}</Text>
        </Td>
        <Td maxW="200px">
            <Text noOfLines={2} fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                {data?.reason}
            </Text>
        </Td>
        <Td fontSize="sm">{data?.employee?.name ?? '—'}</Td>
        <Td>
            {data?.approved_by
                ? <Tag colorScheme="green"  size="sm">Aprovado</Tag>
                : <Tag colorScheme="yellow" size="sm">Pendente</Tag>}
        </Td>
        <Td isNumeric fontSize="xs" whiteSpace="nowrap">
            {data?.created_at
                ? new Date(data.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                : '—'}
        </Td>
    </Row>
)
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Losses() {
    const distributors = useAllDistributors()
    const warehouses   = distributors.data?.filter(d => d.is_warehouse) ?? []

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Perdas de Estoque" />

            <Tabs isLazy colorScheme="red" mt={4}>
                <TabList>
                    <Tab fontSize="sm" gap={1.5}>
                        <Icon name="new" boxSize={3.5} color="red.400" />
                        Registrar Perda
                    </Tab>
                    <Tab fontSize="sm" gap={1.5}>
                        <Icon name="view" boxSize={3.5} />
                        Histórico
                    </Tab>
                </TabList>

                <TabPanels>
                    <TabPanel px={0} pt={5}>
                        <RegisterTab warehouses={warehouses} />
                    </TabPanel>
                    <TabPanel px={0} pt={5}>
                        <HistoryTab warehouses={warehouses} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Container>
    )
}
